import { createApiService } from './api';
import { unwrapPaginatedResponse } from '@/utils/pagination';
import type {
  SubscriptionPlan,
  SubscriptionPlanRequest,
  AffiliatorGroup,
  AffiliatorGroupOnboardingRequest,
  ApiResponse,
  PaginatedResponse,
  ListSubscriptionPlansParams,
  ListAffiliatorsParams,
  BulkImportRequest,
  BulkImportResponse,
  ClonePlanRequest,
  StatusChangeRequest,
  CalculateProfitRequest,
  CalculateProfitResponse,
  ProcessPayoutRequest,
  ProcessPayoutResponse,
  AffiliatorDashboard,
  CodeAvailabilityResponse,
} from '@/types/subscription';

// Subscription Service API Base URL
// Uses nginx reverse proxy path for all environments (K8s, Docker, local via Vite)
const SUBSCRIPTION_API_BASE_URL =
  import.meta.env.VITE_SUBSCRIPTION_API_BASE_URL || '/api/subscription';

const subscriptionApiService = createApiService(SUBSCRIPTION_API_BASE_URL);

// Helper to unwrap ApiResponse
const unwrapResponse = <T>(response: ApiResponse<T> | T): T => {
  // If response is already the data type (no wrapper)
  if (response && typeof response === 'object' && !('success' in response)) {
    return response as T;
  }
  // If response is wrapped in ApiResponse
  const wrapped = response as ApiResponse<T>;
  if (!wrapped.success || !wrapped.data) {
    throw new Error(wrapped.message || 'API request failed');
  }
  return wrapped.data;
};

// ============================================================================
// SubscriptionPlan APIs
// ============================================================================

export const subscriptionPlanApi = {
  // Create plan
  create: async (data: SubscriptionPlanRequest): Promise<SubscriptionPlan> => {
    const response = await subscriptionApiService.post<ApiResponse<SubscriptionPlan>>(
      '/api/v1/subscription-plans',
      data
    );
    return unwrapResponse(response);
  },

  // Bulk import plans
  bulkImport: async (data: BulkImportRequest): Promise<BulkImportResponse> => {
    const response = await subscriptionApiService.post<ApiResponse<BulkImportResponse>>(
      '/api/v1/subscription-plans/import',
      data
    );
    return unwrapResponse(response);
  },

  // Clone plan
  clone: async (planId: string, data?: ClonePlanRequest): Promise<SubscriptionPlan> => {
    const response = await subscriptionApiService.post<ApiResponse<SubscriptionPlan>>(
      `/api/v1/subscription-plans/${planId}/clone`,
      data || {}
    );
    return unwrapResponse(response);
  },

  // List plans with pagination and filters
  list: async (params?: ListSubscriptionPlansParams): Promise<PaginatedResponse<SubscriptionPlan>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.planType) queryParams.append('planType', params.planType);
    if (params?.trialEnabled !== undefined) queryParams.append('trialEnabled', params.trialEnabled.toString());
    if (params?.countryCode) queryParams.append('countryCode', params.countryCode);
    if (params?.paymentGateway) queryParams.append('paymentGateway', params.paymentGateway);
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    const response = await subscriptionApiService.get<ApiResponse<PaginatedResponse<SubscriptionPlan>> | PaginatedResponse<SubscriptionPlan>>(
      `/api/v1/subscription-plans${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse<SubscriptionPlan>(response);
  },

  // Get plan by ID
  getById: async (planId: string): Promise<SubscriptionPlan> => {
    const response = await subscriptionApiService.get<ApiResponse<SubscriptionPlan>>(
      `/api/v1/subscription-plans/${planId}`
    );
    return unwrapResponse(response);
  },

  // Get plan by code
  getByCode: async (planCode: string): Promise<SubscriptionPlan> => {
    const response = await subscriptionApiService.get<ApiResponse<SubscriptionPlan>>(
      `/api/v1/subscription-plans/code/${planCode}`
    );
    return unwrapResponse(response);
  },

  // Get active plans
  getActive: async (params?: Omit<ListSubscriptionPlansParams, 'status'>): Promise<PaginatedResponse<SubscriptionPlan>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.planType) queryParams.append('planType', params.planType);
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    const response = await subscriptionApiService.get<ApiResponse<PaginatedResponse<SubscriptionPlan>> | PaginatedResponse<SubscriptionPlan>>(
      `/api/v1/subscription-plans/active${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse<SubscriptionPlan>(response);
  },

  // Get plans by country
  getByCountry: async (countryCode: string, params?: { page?: number; size?: number; sort?: string }): Promise<PaginatedResponse<SubscriptionPlan>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);

    const query = queryParams.toString();
    const response = await subscriptionApiService.get<ApiResponse<PaginatedResponse<SubscriptionPlan>> | PaginatedResponse<SubscriptionPlan>>(
      `/api/v1/subscription-plans/country/${countryCode}${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse<SubscriptionPlan>(response);
  },

  // Get plans by availability
  getByAvailability: async (params: {
    countryCode: string;
    currencyCode: string;
    paymentGateway: string;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PaginatedResponse<SubscriptionPlan>> => {
    const queryParams = new URLSearchParams();
    queryParams.append('countryCode', params.countryCode);
    queryParams.append('currencyCode', params.currencyCode);
    queryParams.append('paymentGateway', params.paymentGateway);
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.sort) queryParams.append('sort', params.sort);

    const response = await subscriptionApiService.get<ApiResponse<PaginatedResponse<SubscriptionPlan>> | PaginatedResponse<SubscriptionPlan>>(
      `/api/v1/subscription-plans/availability?${queryParams.toString()}`
    );
    return unwrapPaginatedResponse<SubscriptionPlan>(response);
  },

  // Check code availability
  checkCodeAvailability: async (planCode: string): Promise<CodeAvailabilityResponse> => {
    const response = await subscriptionApiService.get<ApiResponse<CodeAvailabilityResponse>>(
      `/api/v1/subscription-plans/code/${planCode}/availability`
    );
    return unwrapResponse(response);
  },

  // Update plan
  update: async (planId: string, data: SubscriptionPlanRequest): Promise<SubscriptionPlan> => {
    const response = await subscriptionApiService.put<ApiResponse<SubscriptionPlan>>(
      `/api/v1/subscription-plans/${planId}`,
      data
    );
    return unwrapResponse(response);
  },

  // Activate plan
  activate: async (planId: string): Promise<SubscriptionPlan> => {
    const response = await subscriptionApiService.patch<ApiResponse<SubscriptionPlan>>(
      `/api/v1/subscription-plans/${planId}/activate`,
      {}
    );
    return unwrapResponse(response);
  },

  // Deactivate plan
  deactivate: async (planId: string): Promise<SubscriptionPlan> => {
    const response = await subscriptionApiService.patch<ApiResponse<SubscriptionPlan>>(
      `/api/v1/subscription-plans/${planId}/deactivate`,
      {}
    );
    return unwrapResponse(response);
  },

  // Archive plan
  archive: async (planId: string): Promise<SubscriptionPlan> => {
    const response = await subscriptionApiService.patch<ApiResponse<SubscriptionPlan>>(
      `/api/v1/subscription-plans/${planId}/archive`,
      {}
    );
    return unwrapResponse(response);
  },

  // Delete plan
  delete: async (planId: string): Promise<void> => {
    const response = await subscriptionApiService.delete<ApiResponse<void>>(
      `/api/v1/subscription-plans/${planId}`
    );
    unwrapResponse(response);
  },
};

// ============================================================================
// AffiliatorGroup APIs
// ============================================================================

export const affiliatorGroupApi = {
  // Onboard (create) affiliator
  onboard: async (data: AffiliatorGroupOnboardingRequest): Promise<AffiliatorGroup> => {
    const response = await subscriptionApiService.post<ApiResponse<AffiliatorGroup>>(
      '/api/v1/affiliators/onboard',
      data
    );
    return unwrapResponse(response);
  },

  // List affiliators with pagination and filters
  list: async (params?: ListAffiliatorsParams): Promise<PaginatedResponse<AffiliatorGroup>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.paymentFrequency) queryParams.append('paymentFrequency', params.paymentFrequency);
    if (params?.commissionModel) queryParams.append('commissionModel', params.commissionModel);
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    const response = await subscriptionApiService.get<ApiResponse<PaginatedResponse<AffiliatorGroup>> | PaginatedResponse<AffiliatorGroup>>(
      `/api/v1/affiliators${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse<AffiliatorGroup>(response);
  },

  // Get affiliator by ID
  getById: async (id: string): Promise<AffiliatorGroup> => {
    const response = await subscriptionApiService.get<ApiResponse<AffiliatorGroup>>(
      `/api/v1/affiliators/${id}`
    );
    return unwrapResponse(response);
  },

  // Get affiliator by code
  getByCode: async (groupCode: string): Promise<AffiliatorGroup> => {
    const response = await subscriptionApiService.get<ApiResponse<AffiliatorGroup>>(
      `/api/v1/affiliators/code/${groupCode}`
    );
    return unwrapResponse(response);
  },

  // Get affiliators by status
  getByStatus: async (status: string, params?: { page?: number; size?: number; sort?: string }): Promise<PaginatedResponse<AffiliatorGroup>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);

    const query = queryParams.toString();
    const response = await subscriptionApiService.get<ApiResponse<PaginatedResponse<AffiliatorGroup>> | PaginatedResponse<AffiliatorGroup>>(
      `/api/v1/affiliators/status/${status}${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse<AffiliatorGroup>(response);
  },

  // Get pending approvals
  getPendingApprovals: async (params?: { page?: number; size?: number; sort?: string }): Promise<PaginatedResponse<AffiliatorGroup>> => {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);

    const query = queryParams.toString();
    const response = await subscriptionApiService.get<ApiResponse<PaginatedResponse<AffiliatorGroup>> | PaginatedResponse<AffiliatorGroup>>(
      `/api/v1/affiliators/approvals/pending${query ? `?${query}` : ''}`
    );
    return unwrapPaginatedResponse<AffiliatorGroup>(response);
  },

  // Get affiliator dashboard
  getDashboard: async (id: string): Promise<AffiliatorDashboard> => {
    const response = await subscriptionApiService.get<ApiResponse<AffiliatorDashboard>>(
      `/api/v1/affiliators/${id}/dashboard`
    );
    return unwrapResponse(response);
  },

  // Update affiliator
  update: async (id: string, data: AffiliatorGroupOnboardingRequest): Promise<AffiliatorGroup> => {
    const response = await subscriptionApiService.put<ApiResponse<AffiliatorGroup>>(
      `/api/v1/affiliators/${id}`,
      data
    );
    return unwrapResponse(response);
  },

  // Approve affiliator
  approve: async (id: string, data?: StatusChangeRequest): Promise<AffiliatorGroup> => {
    const response = await subscriptionApiService.post<ApiResponse<AffiliatorGroup>>(
      `/api/v1/affiliators/${id}/approve`,
      data || {}
    );
    return unwrapResponse(response);
  },

  // Reject affiliator
  reject: async (id: string, data: StatusChangeRequest & { reason: string }): Promise<AffiliatorGroup> => {
    const response = await subscriptionApiService.post<ApiResponse<AffiliatorGroup>>(
      `/api/v1/affiliators/${id}/reject`,
      data
    );
    return unwrapResponse(response);
  },

  // Suspend affiliator
  suspend: async (id: string, data: StatusChangeRequest & { reason: string }): Promise<AffiliatorGroup> => {
    const response = await subscriptionApiService.post<ApiResponse<AffiliatorGroup>>(
      `/api/v1/affiliators/${id}/suspend`,
      data
    );
    return unwrapResponse(response);
  },

  // Reactivate affiliator
  reactivate: async (id: string, data?: StatusChangeRequest): Promise<AffiliatorGroup> => {
    const response = await subscriptionApiService.post<ApiResponse<AffiliatorGroup>>(
      `/api/v1/affiliators/${id}/reactivate`,
      data || {}
    );
    return unwrapResponse(response);
  },

  // Terminate affiliator
  terminate: async (id: string, data: StatusChangeRequest & { reason: string }): Promise<void> => {
    // DELETE endpoint with body - using request method directly to send body
    const response = await subscriptionApiService.request<ApiResponse<void>>(
      `/api/v1/affiliators/${id}/terminate`,
      {
        method: 'DELETE',
        body: JSON.stringify(data),
      }
    );
    unwrapResponse(response);
  },

  // Calculate profit
  calculateProfit: async (data: CalculateProfitRequest): Promise<CalculateProfitResponse> => {
    const response = await subscriptionApiService.post<ApiResponse<CalculateProfitResponse>>(
      '/api/v1/affiliators/profit/calculate',
      data
    );
    return unwrapResponse(response);
  },

  // Process payout
  processPayout: async (id: string, data: ProcessPayoutRequest): Promise<ProcessPayoutResponse> => {
    const response = await subscriptionApiService.post<ApiResponse<ProcessPayoutResponse>>(
      `/api/v1/affiliators/${id}/payout`,
      data
    );
    return unwrapResponse(response);
  },
};

export { subscriptionApiService };
